import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Provider } from 'react-redux';
import { saveUserData, clearUserData, endUserSession, getUserData } from '@/services/API';
import { APP_SELECTION_SESSION_STORAGE_KEY, userTypes} from '../constants/constants';
import authTokenService from '@/services/auth/AuthTokenService';
import config from '../config';
import Amplify, { Auth } from 'aws-amplify';
import App from '@/components/App';
import AmiraSignIn, { AmiraAuthenticator } from '@/components/Login/AmiraSignIn';
import {
  Footer,
  Loading,
  LefthandGlobalNavBar,
  lefthandNavItemsGeneral,
  lefthandNavItemsAdmin
} from '@/components/Global';
import { GetBrandedCopyright, GetBrandedFooter } from '@/services/BrandingService';
import { redirectToLoginOrigin, saveLoginOrigin } from '@/services/loginService';
import UserStoreState from './UserStoreState';
import CalendarInitializer from './CalendarInitializer';
import awsConfig from 'aws-exports';

Amplify.configure(Object.assign({}, awsConfig, {
  Auth: {
    authenticationFlowType: 'USER_PASSWORD_AUTH',
    storage: window.localStorage,
  },
}));

// parse the search params from our current URL
// this is used to (a) deeplink to signup and (b) prepopulate verification
let urlParams = new URLSearchParams(window.location.search);
const AUTH_MODES = {signUp: 'signUp', signup: 'signUp', confirm: 'confirmSignUp', signin: 'signIn'};
const SIGNEDIN_MODE = {signedIn: 'signedIn'};
let token = null;
const sts = new AWS.STS();
let sso = false;
const isDevelopment = window.location.host.includes('localhost')

class AppContainer extends React.Component {
  constructor(props){
    super(props);

    this.state = {
      loading: true,
      userData: null,
      preChecksDone: false,
    }

  }

  componentDidMount(){
    const loginOrigin = urlParams.get("login_origin");
    token = urlParams.get("token");
    const accessToken = urlParams.get("access_token");
    const userId = urlParams.get("userId");

    // Store the access token in the AuthTokenService
    if (accessToken) {
      authTokenService.setAccessToken(accessToken);
    } else {
      console.warn("No access token found in URL payload");
    }

    if (token) {
      sso = true;
      this.assumeRole();
    } else {
      console.warn("No token found in URL parameters");
    }

    if (loginOrigin) {
      saveLoginOrigin(loginOrigin);
    }

    this.setState({ 
      tokenInfo: { 
        token, 
        userId,
        accessToken,
      } 
    });
  }

  getUserDataFromURL(){
    let grade = urlParams.get("grade");
    if(grade) grade = parseInt(grade);

    let schoolId = parseInt(urlParams.get("schoolId"));
    schoolId = !isNaN(schoolId) ? schoolId : null;

    let districtId = parseInt(urlParams.get("districtId"));
    districtId = !isNaN(districtId) ? districtId : null;

    let subdistrictId = (urlParams.get("subdistrictId"));
    subdistrictId = !isNaN(subdistrictId) ? subdistrictId : null;


    const user = {
      attributes: {
        'custom:firstName': urlParams.get("firstName") || "DefaultFirst",
        'custom:lastName': urlParams.get("lastName") || "DefaultLast",
        'custom:sisId': urlParams.get("userId") || "12345678-1234-1234-1234-123456789012",
        'custom:userType': urlParams.get("role") ? userTypes[urlParams.get("role").toUpperCase()] : userTypes.STUDENT,
        grade: grade || 0,
        districtId: districtId || 12591,
        schoolId: schoolId || 12595,
        subdistrictId,
        correlationid: urlParams.get("correlationid"),
        skipMagic: true
      }
    };
    return user;
  }

  //Cache the User Data
  componentDidUpdate(prevProps, prevState){
    if(this.props.authData != prevProps.authData){
      if(this.props.authData){
        this.cacheData(this.props.authData);
      }
    }

    if( !prevState.userData && this.state.userData ){
      this.setState({
        loading: false
      })
    }
  }

  federatedLogIn(sessionToken){
    const expiresIn = 3600;
    const date = new Date();
    const expires_at = expiresIn * 1000 + date.getTime();

    const user = this.getUserDataFromURL();
    return Auth.federatedSignIn('developer', { token: sessionToken, expires_at }, user)
    .then(credentials => {
        return Auth.currentAuthenticatedUser()
        .then((resp)=>{
          this.cacheData(resp);
        })
    });
  }

  assumeRole(){
    const userId = this.state.tokenInfo?.userId || "unknown";
    var params = {
      DurationSeconds: 3600,
      RoleArn: process.env.ASSUME_ROLE_ARN,
      WebIdentityToken: token,
      RoleSessionName: "test"
     };
     sts.assumeRoleWithWebIdentity(params, (function(err, data) {
       if (err) console.log(err, err.stack); // an error occurred
       else {
        AWS.config.update({
          accessKeyId: data.Credentials.AccessKeyId,
          secretAccessKey: data.Credentials.SecretAccessKey,
          sessionToken: data.Credentials.SessionToken
        });
        this.federatedLogIn(data.Credentials.sessionToken);
       }
     }).bind(this));
  }

  cacheData(data){
    data.locale = urlParams.get('locale');
    data.region = urlParams.get('region');
    
    // Add tokenInfo to the data being saved
    if (this.state.tokenInfo) {
      data.tokenInfo = this.state.tokenInfo;
      // If there's an access token, add it directly to the data for easier access
      if (this.state.tokenInfo.accessToken) {
        data.accessToken = this.state.tokenInfo.accessToken;
      }
    }
    
    saveUserData(data)
    .then(resp =>{
      this.setState({
        userData: resp,
      });
    })
    .catch((err) =>{
      console.log("ERROR IN SETTING LOGGED IN USER!", err)
      endUserSession();
    });
  }


  render() {
    const isAdminUser = (({userType, reportingRole}) => (userType === userTypes.SCHOOL_ADMIN || userType === userTypes.DISTRICT_ADMIN || reportingRole))(getUserData());

    // The user must be Authenticated to access the App
    if(SIGNEDIN_MODE[this.props.authState] && this.state.loading){
      return <Loading/>
    }
    if(!SIGNEDIN_MODE[this.props.authState] || !this.state.userData){
      return null;
    }
    const { userType } = this.state.userData;
    const appConfig = config[userType];
    const appLinks = isAdminUser ? lefthandNavItemsAdmin : lefthandNavItemsGeneral;
    return (
      <>
        {/* TODO: Uncomment when IDserv will redirect to AEC */}
        {/* {sessionStorage.getItem(APP_SELECTION_SESSION_STORAGE_KEY) && ( */}
          <LefthandGlobalNavBar items={appLinks} />
        {/* )} */}
        <App
          {...this.props}
          userData={this.state.userData}
          config={appConfig}
          region={urlParams.get('region')}
          accessToken={this.state.userData.accessToken}
        />
        <UserStoreState userData={this.state.userData} />
        <CalendarInitializer />
      </>
    );
  }
}


// export default class AppWithAuth extends React.Component {
//   render(){
const AppWithAuth = (props) => {
  const isSSO = !!urlParams.get('token');
  const authState = isSSO ? 'signedIn' : AUTH_MODES[urlParams.get('authState')] || AUTH_MODES.signin;
  return (
<Provider store={props.store}>
  <Router basename={GITHUB_PAGES_ROOT_PATH}>
    <AmiraAuthenticator
      includeGreetings={false}
      hideDefault={true}
      authState={authState}
      federated={isSSO}
      hide={isSSO ? ["AmiraSignIn"] : []}
    >
      <AmiraSignIn/>
      <AppContainer />
      <Footer />
    </AmiraAuthenticator>
  </Router>
</Provider>
)};

export {
  AppWithAuth as default,
  AppWithAuth,
  AppContainer,
};